import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Grid,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Visibility,
  LocalShipping,
  Receipt,
  CheckCircle,
  Refresh,
  ShoppingCart,
  AttachMoney,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { salesOrderAPI } from '../../services/api';
import { toast } from 'react-toastify';
import useDebounce from '../../utils/useDebounce';
import {
  ListPageHeader,
  FilterBar,
  StatusChip,
  tableHeadRowStyles,
  tableBodyRowStyles,
  tablePaginationStyles,
  primaryLinkStyles,
  standardTextStyles,
  secondaryTextStyles,
  emptyStateTextStyles,
  statusChipStyles,
  outlinedChipStyles,
} from '../../components/common';

const SalesOrdersList = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [statusFilter, setStatusFilter] = useState('');
  const [stats, setStats] = useState(null);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await salesOrderAPI.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch,
        status: statusFilter || undefined,
      });
      setOrders(response.data.orders);
      setTotalItems(response.data.pagination.totalItems);
      setStats(response.data.stats);
    } catch (error) {
      toast.error('Failed to load sales orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, statusFilter]);

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      await salesOrderAPI.updateStatus(id, newStatus);
      toast.success('Status updated successfully');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleCreateInvoice = async (orderId) => {
    try {
      const response = await salesOrderAPI.getById(orderId);
      // Navigate to invoice creation
      navigate(`/invoices/new?orderId=${orderId}`);
    } catch (error) {
      toast.error('Failed to create invoice');
    }
  };

  const getStatValue = (status) => {
    const stat = stats?.find(s => s._id === status);
    return { count: stat?.count || 0, total: stat?.total || 0 };
  };

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: 'calc(100vh - 64px)', px: { xs: 1, sm: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
      {/* Header */}
      <ListPageHeader
        title="Sales Orders"
        subtitle="Manage your sales orders and track deliveries"
        buttonText="Refresh"
        buttonIcon={<Refresh />}
        onButtonClick={fetchOrders}
      />

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'primary.light' }}>
                  <ShoppingCart sx={{ color: 'primary.main' }} />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{totalItems}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Orders</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#dbeafe' }}>
                  <CheckCircle sx={{ color: '#2563eb' }} />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{getStatValue('confirmed').count}</Typography>
                  <Typography variant="body2" color="text.secondary">Confirmed</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#e0e7ff' }}>
                  <LocalShipping sx={{ color: '#6366f1' }} />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{getStatValue('shipped').count}</Typography>
                  <Typography variant="body2" color="text.secondary">Shipped</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#d1fae5' }}>
                  <AttachMoney sx={{ color: '#059669' }} />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    ₹{((getStatValue('delivered').total + getStatValue('confirmed').total) / 100000).toFixed(1)}L
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Revenue</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search orders..."
        filters={[
          {
            name: 'status',
            label: 'Status',
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
              { value: 'confirmed', label: 'Confirmed' },
              { value: 'processing', label: 'Processing' },
              { value: 'shipped', label: 'Shipped' },
              { value: 'delivered', label: 'Delivered' },
              { value: 'cancelled', label: 'Cancelled' },
            ],
          },
        ]}
      />

      {/* Orders Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Table size="small" sx={{ minWidth: { xs: 700, md: 'auto' } }}>
              <TableHead>
                <TableRow sx={tableHeadRowStyles}>
                  <TableCell>Order #</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Date</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Payment</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography sx={emptyStateTextStyles}>No sales orders found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  orders.map((order) => (
                    <TableRow 
                      key={order._id} 
                      hover
                      sx={tableBodyRowStyles}
                      onClick={() => navigate(`/sales-orders/${order._id}`)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Typography sx={primaryLinkStyles}>
                          {order.orderNumber}
                        </Typography>
                        {order.quotation && (
                          <Typography sx={secondaryTextStyles}>
                            From: {order.quotation.quotationNumber}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography sx={standardTextStyles}>
                          {order.customerSnapshot?.name || order.customer?.name}
                        </Typography>
                        <Typography sx={secondaryTextStyles}>
                          {order.customerSnapshot?.company || order.customer?.company}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography sx={standardTextStyles}>
                          {new Date(order.orderDate).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ ...standardTextStyles, fontWeight: 600 }}>
                          ₹{order.total?.toLocaleString('en-IN')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={order.status} sx={statusChipStyles} />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        <StatusChip
                          status={order.paymentStatus}
                          label={order.paymentStatus}
                          variant="outlined"
                          sx={outlinedChipStyles}
                        />
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/sales-orders/${order._id}`)}
                          >
                            <Visibility sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        {order.status === 'confirmed' && (
                          <Tooltip title="Mark as Shipped">
                            <IconButton
                              size="small"
                              onClick={() => handleUpdateStatus(order._id, 'shipped')}
                              sx={{ color: '#6366f1' }}
                            >
                              <LocalShipping sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {order.status === 'shipped' && (
                          <Tooltip title="Mark as Delivered">
                            <IconButton
                              size="small"
                              onClick={() => handleUpdateStatus(order._id, 'delivered')}
                              sx={{ color: '#28a745' }}
                            >
                              <CheckCircle sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Create Invoice">
                          <IconButton
                            size="small"
                            onClick={() => handleCreateInvoice(order._id)}
                            sx={{ color: '#875A7B' }}
                          >
                            <Receipt sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              sx={tablePaginationStyles}
            />
          </>
        )}
      </TableContainer>
    </Box>
  );
};

export default SalesOrdersList;
